/**
 * IndexedDB queue for recording chunks.
 *
 * Chunks are the one artefact we cannot regenerate: if a 10-second slice of
 * video is lost to a dropped Wi-Fi connection, it is gone. So every chunk is
 * written to IndexedDB *before* any upload is attempted, and only deleted once
 * the server has acknowledged it. A tab crash, a reload, or two hours offline
 * all resolve the same way — the queue is still on disk and drains later.
 *
 * IndexedDB (not localStorage) because Blobs are stored natively, with no
 * base64 inflation and no 5 MB cap.
 *
 * `chunk_index` is allocated here and persisted, so a reload continues the
 * sequence instead of restarting at 0 and overwriting earlier chunks (uploads
 * are idempotent *per index* — a reused index silently destroys video).
 *
 * **Every operation here is bounded, and every one settles.** An IndexedDB
 * request that is listened to only via `request.onerror` does NOT settle when
 * the TRANSACTION aborts — on a quota failure, an eviction, or a connection
 * closed underneath us. A promise that never settles inside the uploader's
 * drain loop is not a slow upload; it is an uploader that is dead for the rest
 * of the exam and says nothing, which is how one attempt recorded four minutes
 * of a twelve-minute paper with no error anywhere. So: transaction handlers on
 * everything, a timeout on everything, and a cached connection that is dropped
 * the moment it closes.
 */
import type { RecordingSource } from "@exam/shared";

const DB_NAME = "exam-proctor-media";
const DB_VERSION = 1;
const CHUNK_STORE = "chunks";
const META_STORE = "meta";

export interface PendingChunk {
  /** `${attemptId}:${source}:${chunkIndex}` — stable, so re-adding is a no-op. */
  id: string;
  attemptId: string;
  source: RecordingSource;
  chunkIndex: number;
  /**
   * Which MediaRecorder session produced it. Only the first blob of a session
   * carries the WebM header, so the player must not concatenate across a
   * change of this — see `recorder.ts`.
   */
  sessionIdx: number;
  /**
   * Server-anchored epoch ms at which this chunk's recording began. The API
   * derives the master-timeline `offset_ms` from it (`started_ms - started_at`),
   * so it must be absolute — and must come from the ServerClock, never from
   * `Date.now()`, or the two recordings will not line up.
   */
  startedMs: number;
  durationMs: number;
  blob: Blob;
  /** How many upload attempts have failed so far. */
  attempts: number;
  /**
   * The server said it would never accept this chunk (the upload window for
   * the attempt has closed).
   *
   * Kept out of the upload queue rather than deleted. Deleting was the only
   * irreversible act in the pipeline and it fired at the worst moment — a
   * slow uplink builds a backlog all exam, the window then closes, and the
   * client destroys that backlog one chunk at a time, silently. The bytes now
   * survive here until `purgeChunksOlderThan` clears them on its own
   * schedule, so a mistake is recoverable and the count is reportable.
   */
  refused?: boolean;
  /**
   * LOCAL `Date.now()` at which this chunk was queued — not server-anchored,
   * unlike `startedMs`. It is only ever compared against other local
   * timestamps: drain order, and the stale-chunk purge. Nothing on the
   * timeline is derived from it, so client clock skew does not matter here.
   */
  queuedAtMs: number;
}

/**
 * No single IndexedDB operation here is worth waiting on for longer than this.
 * Failing loudly re-queues the work and keeps the recording alive; hanging
 * ends it.
 */
const IDB_TIMEOUT_MS = 15_000;

export class ChunkStoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChunkStoreError";
  }
}

/** Rejects rather than hanging, whatever the underlying promise does. */
function withTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new ChunkStoreError(`${label} timed out`)),
      IDB_TIMEOUT_MS,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Drop the cached connection so the next call reopens. A closed handle throws
 * `InvalidStateError` on every subsequent `transaction()`, which without this
 * would mean the queue stays broken for the rest of the exam.
 */
function forgetDb(): void {
  dbPromise = null;
}

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= withTimeout(
    new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CHUNK_STORE)) {
          const store = db.createObjectStore(CHUNK_STORE, { keyPath: "id" });
          // Drain order: oldest chunk of a given recording first.
          store.createIndex("by_attempt_source", ["attemptId", "source"]);
          store.createIndex("by_queued_at", "queuedAtMs");
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: "key" });
        }
      };

      req.onsuccess = () => {
        const db = req.result;
        // A connection can be closed under us by an eviction or another tab
        // upgrading the schema. Either way this handle is finished.
        db.onclose = forgetDb;
        db.onversionchange = () => {
          db.close();
          forgetDb();
        };
        resolve(db);
      };
      req.onerror = () =>
        reject(req.error ?? new ChunkStoreError("indexedDB open failed"));
      req.onblocked = () =>
        reject(new ChunkStoreError("indexedDB open was blocked"));
    }),
    "indexedDB open",
  ).catch((err: unknown) => {
    forgetDb(); // never cache a failed open
    throw err;
  });
  return dbPromise;
}

/**
 * Runs one request inside its own transaction, settling on whichever of the
 * request or the transaction resolves first.
 *
 * Listening only to `request.onerror` is the bug this exists to prevent: a
 * transaction that aborts (quota exceeded, eviction, connection closed) may
 * never fire it, and the caller waits for a result that is never coming.
 */
function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
  label = "indexedDB request",
): Promise<T> {
  return openDb().then((db) =>
    withTimeout(
      new Promise<T>((resolve, reject) => {
        let transaction: IDBTransaction;
        try {
          transaction = db.transaction(storeName, mode);
        } catch (err) {
          // Almost always InvalidStateError on a closed connection.
          forgetDb();
          reject(err);
          return;
        }

        let settled = false;
        const fail = (err: unknown) => {
          if (settled) return;
          settled = true;
          reject(err ?? new ChunkStoreError(`${label} failed`));
        };

        transaction.onabort = () =>
          fail(transaction.error ?? new ChunkStoreError(`${label} aborted`));
        transaction.onerror = () =>
          fail(transaction.error ?? new ChunkStoreError(`${label} errored`));

        let req: IDBRequest<T>;
        try {
          req = run(transaction.objectStore(storeName));
        } catch (err) {
          fail(err);
          return;
        }

        req.onsuccess = () => {
          if (settled) return;
          settled = true;
          resolve(req.result);
        };
        req.onerror = () => fail(req.error);
      }),
      label,
    ),
  );
}

/**
 * Is there room left to keep recording?
 *
 * Chunks are only deleted once the server has acknowledged them, so a long
 * upload outage grows the queue without bound until the browser refuses to
 * store any more — and a rejected write is where the footage actually stops.
 * Knowing beforehand is what lets the UI say so while there is still time to
 * do something about it.
 */
export async function storagePressure(): Promise<{
  usage: number;
  quota: number;
  ratio: number;
} | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    if (quota <= 0) return null;
    return { usage, quota, ratio: usage / quota };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Chunk index allocation                                              */
/* ------------------------------------------------------------------ */

function indexKey(attemptId: string, source: RecordingSource): string {
  return `next_index:${attemptId}:${source}`;
}

function sessionKey(attemptId: string, source: RecordingSource): string {
  return `next_session:${attemptId}:${source}`;
}

/**
 * Reserves the next `chunk_index` for a recording and persists it immediately,
 * so a crash between allocation and upload still advances the sequence.
 */
export async function allocateChunkIndex(
  attemptId: string,
  source: RecordingSource,
): Promise<number> {
  return allocateCounter(indexKey(attemptId, source));
}

/** Read-and-increment a persisted counter, in one transaction. */
async function allocateCounter(key: string): Promise<number> {
  const db = await openDb();

  return withTimeout(
    new Promise<number>((resolve, reject) => {
      let transaction: IDBTransaction;
      try {
        transaction = db.transaction(META_STORE, "readwrite");
      } catch (err) {
        forgetDb();
        reject(err);
        return;
      }

      let settled = false;
      const fail = (err: unknown) => {
        if (settled) return;
        settled = true;
        reject(err ?? new ChunkStoreError("index allocation failed"));
      };
      // Two chained requests in one transaction — an abort between them would
      // otherwise leave this pending forever.
      transaction.onabort = () => fail(transaction.error);
      transaction.onerror = () => fail(transaction.error);

      const store = transaction.objectStore(META_STORE);
      const getReq = store.get(key);

      getReq.onsuccess = () => {
        const current = (
          getReq.result as { key: string; value: number } | undefined
        )?.value;
        const next = typeof current === "number" ? current : 0;
        const putReq = store.put({ key, value: next + 1 });
        putReq.onsuccess = () => {
          if (settled) return;
          settled = true;
          resolve(next);
        };
        putReq.onerror = () => fail(putReq.error);
      };
      getReq.onerror = () => fail(getReq.error);
    }),
    "allocate chunk index",
  );
}

/**
 * Reserves the next MediaRecorder session number for a recording.
 *
 * Persisted for the same reason `chunk_index` is: recording now begins during
 * setup and carries on into the exam, which are two different pages and
 * therefore two different recorder instances. An instance that started again
 * at session 0 would tell the player its chunks belong with the setup
 * footage — and they would be concatenated across a WebM header boundary,
 * which makes everything after it undecodable.
 */
export async function allocateSessionIdx(
  attemptId: string,
  source: RecordingSource,
): Promise<number> {
  return allocateCounter(sessionKey(attemptId, source));
}

/* ------------------------------------------------------------------ */
/* Queue operations                                                    */
/* ------------------------------------------------------------------ */

export async function enqueueChunk(
  chunk: Omit<PendingChunk, "id" | "attempts">,
): Promise<PendingChunk> {
  const record: PendingChunk = {
    ...chunk,
    id: `${chunk.attemptId}:${chunk.source}:${chunk.chunkIndex}`,
    attempts: 0,
  };
  await tx(CHUNK_STORE, "readwrite", (s) => s.put(record), "enqueue chunk");
  return record;
}

export async function listPendingChunks(
  attemptId?: string,
): Promise<PendingChunk[]> {
  const all = await tx<PendingChunk[]>(
    CHUNK_STORE,
    "readonly",
    (s) => s.getAll() as IDBRequest<PendingChunk[]>,
    "list pending chunks",
  );
  const rows = attemptId ? all.filter((c) => c.attemptId === attemptId) : all;
  // Oldest first, so the recording drains in order and playback gaps close
  // from the front rather than appearing scattered.
  return rows.sort((a, b) => a.queuedAtMs - b.queuedAtMs);
}

export async function removeChunk(id: string): Promise<void> {
  await tx(CHUNK_STORE, "readwrite", (s) => s.delete(id), "remove chunk");
}

export async function markChunkFailed(chunk: PendingChunk): Promise<void> {
  await tx(
    CHUNK_STORE,
    "readwrite",
    (s) => s.put({ ...chunk, attempts: chunk.attempts + 1 }),
    "mark chunk failed",
  );
}

/**
 * Take a permanently-refused chunk out of the upload rotation, keeping the
 * bytes.
 *
 * Marking it merely "failed" is not enough: the drain always takes the oldest
 * queued chunk, so a chunk the server keeps refusing would be picked again on
 * the very next pass and the loop would hammer it forever.
 */
export async function markChunkRefused(chunk: PendingChunk): Promise<void> {
  await tx(
    CHUNK_STORE,
    "readwrite",
    (s) => s.put({ ...chunk, attempts: chunk.attempts + 1, refused: true }),
    "mark chunk refused",
  );
}

/** Chunks the server refused outright — retained, but never retried. */
export async function countRefusedChunks(attemptId?: string): Promise<number> {
  const rows = await listPendingChunks(attemptId);
  return rows.filter((c) => c.refused).length;
}

export async function countPendingChunks(attemptId?: string): Promise<number> {
  return (await listPendingChunks(attemptId)).length;
}

/** Total queued bytes — surfaced in the UI so a growing backlog is visible. */
export async function pendingBytes(attemptId?: string): Promise<number> {
  const rows = await listPendingChunks(attemptId);
  return rows.reduce((sum, c) => sum + c.blob.size, 0);
}

/**
 * Housekeeping for chunks belonging to attempts that are long finished — the
 * API rejects uploads more than 5 minutes after submit, so anything older is
 * dead weight that would otherwise be retried forever.
 */
export async function purgeChunksOlderThan(ageMs: number): Promise<number> {
  const cutoff = Date.now() - ageMs;
  const rows = await listPendingChunks();
  const stale = rows.filter((c) => c.queuedAtMs < cutoff);
  await Promise.all(stale.map((c) => removeChunk(c.id)));
  return stale.length;
}
